/**
 * قراءة/كتابة مبسَّطة لبلوكات الهوم: صورة + عنوان + نصّ + زرّ فقط.
 * تقرأ وتكتب على نفس مفاتيح app_settings الخاصّة بكلّ بلوك (بلا جدول جديد)،
 * وتدمج التعديل فوق القيمة الحاليّة حتى لا تُفقد أيّ حقول متقدّمة.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";
import {
  SIMPLE_BLOCK_KEYS,
  EMPTY_SIMPLE_FIELDS,
  type SimpleBlockFields,
} from "@/lib/simple-blocks";

function pickFields(raw: unknown): SimpleBlockFields {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  const mode = s(o.imageMode);
  const dev = s(o.devices);
  return {
    enabled: typeof o.enabled === "boolean" ? o.enabled : EMPTY_SIMPLE_FIELDS.enabled,
    imageUrl: s(o.imageUrl),
    imageMode: mode === "full" ? "full" : "collage",
    bgColor: s(o.bgColor),
    devices: dev === "mobile" || dev === "desktop" ? dev : "all",
    titleAr: s(o.titleAr),
    titleEn: s(o.titleEn),
    bodyAr: s(o.bodyAr),
    bodyEn: s(o.bodyEn),
    ctaLabelAr: s(o.ctaLabelAr),
    ctaLabelEn: s(o.ctaLabelEn),
    ctaHref: s(o.ctaHref),
  };
}

export const getSimpleBlocks = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({}).parse(d ?? {}),
  )
  .handler(async ({ data: input }) => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: rows } = await supabaseAdmin
    .from("app_settings")
    .select("key,value")
    .in("key", SIMPLE_BLOCK_KEYS.map((k) => k));
  const map: Record<string, SimpleBlockFields> = {};
  for (const r of rows ?? []) {
    map[r.key as string] = pickFields(r.value);
  }
  return { blocks: map };
});

export const setSimpleBlock = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        key: z.string().refine((k) => SIMPLE_BLOCK_KEYS.includes(k), "مفتاح غير مسموح"),
        enabled: z.boolean(),
        imageUrl: z.string().max(800),
        imageMode: z.enum(["collage", "full"]).default("collage"),
        bgColor: z.string().max(20).default(""),
        devices: z.enum(["all", "mobile", "desktop"]).default("all"),
        titleAr: z.string().max(240),
        titleEn: z.string().max(240),
        bodyAr: z.string().max(4000),
        bodyEn: z.string().max(4000),
        ctaLabelAr: z.string().max(80),
        ctaLabelEn: z.string().max(80),
        ctaHref: z.string().max(500),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { key: rawKey, ...fields } = data;
    const key = rawKey;
    const { data: row } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", key)
      .maybeSingle();
    const current =
      row?.value && typeof row.value === "object" ? (row.value as Record<string, unknown>) : {};
    const value = { ...current, ...fields };
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key,
        value,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);
    return { ok: true, fields: pickFields(value) };
  });
